/*
 * "Complete your kit" strip (snippets/ms-kit.liquid), adapted from Curtis's handoff (2026-10-15).
 *
 * - Scrolls slowly on its own (scrollLeft, so swiping always works) and loops over a second copy of
 *   the cards. Never moves for prefers-reduced-motion, when the cards fit without scrolling, when the
 *   strip is off screen or when the tab is hidden.
 * - Q52 option B (Curtis, 19 Oct 2026): the first touch, click, wheel, keyboard focus or (mouse) hover
 *   stops it, and it stays stopped: the shopper swipes it by hand and taps "+ Add" while it stands still.
 *   Only the visible Pause/Play button ([data-ms-toggle], aria-label "Pause scrolling" / "Play scrolling") starts it again.
 * - "+ Add" sends the theme's `ra:cart-add` event (assets/ra-pdp-cart.js) with `silent: true`: one
 *   /cart/add.js request, the cart count updates, and Rebuy's Smart Cart does not open.
 * - "Choose size" (products with several variants) opens the product's picker; its Add sends the
 *   chosen variant the same way. Without <dialog> support it goes to the product page.
 */
(function () {
  if (window.msKitLoaded) return;
  window.msKitLoaded = true;
  var reduceMQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var BUSY_TIMEOUT = 15000;

  /* Review M2 (2026-10-18): the add settles only when assets/ra-pdp-cart.js's job does (every request there is
     time-limited), so the button stays busy until then. Only an add nobody heard (detail.accepted unset: nothing
     was sent) is given up after BUSY_TIMEOUT. */
  function cartAdd(host, id) {
    return new Promise(function (resolve, reject) {
      var timer = 0;
      var detail = {
        items: [{ id: Number(id), quantity: 1 }],
        source: 'ms-kit',
        silent: true,
        resolve: function (cart) { clearTimeout(timer); resolve(cart); },
        reject: function (error) { clearTimeout(timer); reject(error); }
      };
      host.dispatchEvent(new CustomEvent('ra:cart-add', { bubbles: true, detail: detail }));
      timer = setTimeout(function () { if (!detail.accepted) reject(new Error('timeout')); }, BUSY_TIMEOUT);
    });
  }

  /* Pass 4 follow-up A5 (2026-10-19): the same words as the deal boxes (assets/ra-ls1.js, ra-reel-buybox.js), never
     "try again" when the add may have landed (.uncertain), nothing was sent (.offline), or Shopify added only what was
     in stock (422; assets/ra-pdp-cart.js hands over the cart as it is now in error.cart). Returns '' otherwise. */
  function whyNot(error, id, name) {
    if (!error) return '';
    if (error.uncertain || error.offline) return error.message;
    if (error.status !== 422) return '';
    var n = 0;
    var items = (error.cart && error.cart.items) || [];
    for (var i = 0; i < items.length; i++) if (Number(items[i].variant_id) === Number(id)) n += Number(items[i].quantity) || 0;
    if (n > 0) return 'Only ' + n + ' of ' + name + ' in stock, so your cart now has ' + n + '.';
    if (error.cart) return name + ' is out of stock.';
    return (String(error.description || '').trim() + ' Check your cart before adding again.').trim();
  }
  /* a plain failure (nothing was added): Shopify's own reason, else try again */
  function reason(error) {
    var m = error && error.message;
    return m && m !== 'timeout' ? m : 'Please try again.';
  }

  function init(kit) {
    if (kit.__msInit) return;
    var first = kit.__msFirst; kit.__msFirst = null;
    kit.__msInit = true;
    var vp = kit.querySelector('.ms-viewport');
    var track = kit.querySelector('.ms-track');
    var firstClone = track.querySelector('[data-ms-clone]');
    var err = kit.querySelector('[data-ms-err]');
    var live = kit.querySelector('[data-ms-live]');
    var addedLabel = kit.getAttribute('data-added') || '✓ Added';
    var speed = parseFloat(kit.getAttribute('data-speed')) || 18; /* px per second */
    var toggle = kit.querySelector('[data-ms-toggle]');
    var paused = false, visible = false, pos = 0, last = 0, raf = 0, startAt = 0, startT;

    function say(text) { if (live) live.textContent = text; }
    function showError(text) {
      if (!err) return;
      err.textContent = text || '';
      err.hidden = !text;
    }

    /* ---------- motion ---------- */
    /* The loop width is measured on load and resize only, so a frame of motion reads no layout. */
    var loopW = -1;
    function loopWidth() {
      if (loopW < 0) loopW = firstClone ? firstClone.offsetLeft - track.firstElementChild.offsetLeft : 0;
      return loopW;
    }
    function checkFit() {
      loopW = -1;
      if (!firstClone) return;
      kit.classList.remove('ms-fit-off');
      kit.classList.toggle('ms-fit-off', loopWidth() <= vp.clientWidth);
      run();
    }
    function motionOff() { return reduceMQ.matches || kit.classList.contains('ms-fit-off') || !firstClone || kit.classList.contains('ms-dismissed'); }
    /* It waits 1.5 s after first coming into view, so the first card is seen whole before it moves. */
    function moving() { return !paused && visible && startAt && Date.now() >= startAt && !document.hidden && !motionOff() && vp.offsetParent !== null; }
    /* per frame: the same checks minus the layout read (the strip hidden by an ancestor also leaves view, so IntersectionObserver stops it) */
    function stillMoving() { return !paused && visible && !document.hidden && !motionOff(); }
    function tick(t) {
      raf = 0;
      if (!stillMoving()) { last = 0; return; }
      var dt = last ? Math.min((t - last) / 1000, 0.1) : 0;
      last = t;
      /* no scrollLeft read here: a swipe, hover or focus pauses the strip, and run() picks the position up again */
      pos += speed * dt;
      var w = loopWidth();
      if (w > 0 && pos >= w) pos -= w;
      vp.scrollLeft = pos;
      raf = requestAnimationFrame(tick);
    }
    function run() { if (!raf && moving()) { last = 0; pos = vp.scrollLeft; raf = requestAnimationFrame(tick); } }
    function setPaused(p) {
      paused = p;
      kit.classList.toggle('ms-is-paused', p);
      /* M3 (Curtis 2026-10-19): the label says what a tap does next, in step with every pause (touch, hover, focus, the
         button) and play; no aria-pressed beside it, so a screen reader never hears "Play scrolling, pressed" */
      if (toggle) toggle.setAttribute('aria-label', p ? 'Play scrolling' : 'Pause scrolling');
    }
    /* Every stop is for good: nothing below starts the strip again except the Pause/Play button. */
    function pause() { setPaused(true); }
    function play() {
      if (kit.querySelector('dialog[open]')) return;
      setPaused(false);
      run();
    }
    function onToggle(e) { return !!(toggle && e && e.target && e.target.nodeType === 1 && toggle.contains(e.target)); }
    function interact(e) { if (!onToggle(e)) pause(); }
    /* hover stops it only with a real mouse or trackpad; a tap's emulated mouse events are caught by touchstart first */
    var hoverMQ = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : { matches: false };

    ['pointerdown', 'touchstart', 'click', 'focusin', 'wheel'].forEach(function (t) {
      kit.addEventListener(t, interact, { passive: true });
    });
    kit.addEventListener('mouseenter', function () { if (hoverMQ.matches) pause(); });
    if (toggle) toggle.addEventListener('click', function () { if (paused) play(); else pause(); });
    if (reduceMQ.addEventListener) reduceMQ.addEventListener('change', run);
    document.addEventListener('visibilitychange', run);
    window.addEventListener('resize', checkFit);
    window.addEventListener('load', checkFit);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        visible = es[es.length - 1].isIntersecting;
        if (visible && !startAt) { startAt = Date.now() + 1500; startT = setTimeout(run, 1550); }
        run();
      }).observe(kit);
    } else {
      visible = true;
      startAt = Date.now() + 1500;
      startT = setTimeout(run, 1550);
    }

    /* While swiping by hand, keep the loop seamless. */
    vp.addEventListener('scroll', function () {
      if (motionOff() || !paused) return;
      var w = loopWidth();
      if (w > 0 && vp.scrollLeft >= w) { vp.scrollLeft -= w; pos = vp.scrollLeft; }
    }, { passive: true });

    /* ---------- sticky option ---------- */
    if (kit.classList.contains('ms-sticky-mobile')) {
      var setH = function () { document.body.style.setProperty('--ms-sticky-h', kit.offsetHeight + 'px'); };
      document.body.classList.add('ms-has-sticky');
      setH();
      if ('ResizeObserver' in window) new ResizeObserver(setH).observe(kit);
    }

    /* ---------- adding ---------- */
    function buttonsFor(productId) {
      return kit.querySelectorAll('.ms-add[data-ms-product="' + productId + '"]');
    }
    function markAdded(productId) {
      Array.prototype.forEach.call(buttonsFor(productId), function (b) {
        setText(b, addedLabel);
        b.classList.add('is-added');
        if (b.hasAttribute('data-ms-id')) b.setAttribute('aria-disabled', 'true');
      });
    }
    /* text edited in place: a new text node restyles the whole page (about 20ms on a slow phone) */
    function setText(b, text) {
      var f = b.firstChild;
      if (f && f.nodeType === 3 && !f.nextSibling) { if (f.data !== text) f.data = text; }
      else if (b.textContent !== text) b.textContent = text;
    }
    function setBusy(list, busy, text) {
      Array.prototype.forEach.call(list, function (b) {
        if (busy) b.setAttribute('aria-busy', 'true');
        else b.removeAttribute('aria-busy');
        if (text !== undefined) setText(b, text);
      });
    }
    function titleOf(btn) {
      var item = btn.closest('.ms-item');
      var t = item && item.querySelector('.ms-title');
      return t ? t.textContent.trim() : 'Item';
    }

    function addSingle(btn) {
      if (btn.getAttribute('aria-busy') === 'true' || btn.getAttribute('aria-disabled') === 'true') return;
      var productId = btn.getAttribute('data-ms-product');
      var all = buttonsFor(productId);
      var label = btn.getAttribute('data-ms-label') || '+ Add';
      var title = titleOf(btn);
      showError('');
      setBusy(all, true, '…');
      cartAdd(btn, btn.getAttribute('data-ms-id')).then(
        function () {
          setBusy(all, false);
          markAdded(productId);
          say(title + ' added to cart');
        },
        function (error) {
          setBusy(all, false, label);
          var why = whyNot(error, btn.getAttribute('data-ms-id'), title);
          showError(why || 'Could not add ' + title + '. ' + reason(error));
          say(why || 'Could not add ' + title);
        }
      );
    }

    function openPicker(btn) {
      var dialog = document.getElementById(btn.getAttribute('data-ms-pick'));
      if (!dialog || typeof dialog.showModal !== 'function') {
        window.location.assign(btn.getAttribute('data-ms-url'));
        return;
      }
      dialog.__msOpener = btn;
      var e = dialog.querySelector('[data-ms-pick-err]');
      if (e) { e.hidden = true; e.textContent = ''; }
      pause();
      dialog.showModal();
      var first = dialog.querySelector('input:checked') || dialog.querySelector('input:not(:disabled)');
      if (first) first.focus();
    }

    Array.prototype.forEach.call(kit.querySelectorAll('dialog.ms-pick'), function (dialog) {
      var form = dialog.querySelector('[data-ms-form]');
      var go = form.querySelector('[type="submit"]');
      var e = form.querySelector('[data-ms-pick-err]');
      var goLabel = go.textContent;
      dialog.addEventListener('click', function (ev) {
        if (ev.target.closest('[data-ms-close]')) { dialog.close(); return; }
        if (ev.target === dialog) {
          var r = dialog.getBoundingClientRect();
          if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) dialog.close();
        }
      });
      dialog.addEventListener('close', function () {
        var opener = dialog.__msOpener;
        if (opener && opener.tabIndex !== -1) opener.focus();
      });
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        if (go.getAttribute('aria-busy') === 'true') return;
        var chosen = form.querySelector('input[name="id"]:checked');
        if (!chosen) {
          e.textContent = 'Choose a size first.';
          e.hidden = false;
          return;
        }
        var productId = form.getAttribute('data-ms-product');
        var title = dialog.querySelector('.ms-pick__title').textContent.trim();
        var size = chosen.closest('label').querySelector('.ms-pick__name').textContent.trim();
        e.hidden = true;
        go.setAttribute('aria-busy', 'true');
        go.textContent = 'Adding…';
        cartAdd(form, chosen.value).then(
          function () {
            go.removeAttribute('aria-busy');
            go.textContent = goLabel;
            chosen.checked = false;
            dialog.close();
            markAdded(productId);
            say(title + ', ' + size + ', added to cart');
          },
          function (error) {
            go.removeAttribute('aria-busy');
            go.textContent = goLabel;
            /* a sized product (Rod Sleeve): "Only 2 of 7–8 ft in stock…", as the Rod Sleeve box says it */
            var why = whyNot(error, chosen.value, size || title);
            e.textContent = why || 'Could not add it. ' + reason(error);
            e.hidden = false;
          }
        );
      });
    });

    kit.addEventListener('click', function (ev) {
      var b = ev.target.closest('.ms-add');
      if (b && kit.contains(b)) {
        if (b.hasAttribute('data-ms-pick')) openPicker(b);
        else addSingle(b);
        return;
      }
      if (ev.target.closest('.ms-close')) {
        kit.classList.add('ms-dismissed');
        document.body.classList.remove('ms-has-sticky');
      }
    });

    checkFit();
    /* set up by the shopper's own first touch, click or focus (see soon()): that interaction stops it too */
    if (first) interact(first);
    else if (hoverMQ.matches && kit.matches(':hover')) pause();
    /* The right-aligned hint moves when the web font swaps in (a layout shift at 1440, where the strip
       is on the first screen). It stays invisible, so untracked, until the fonts are in. */
    var fontsIn = function () { loopW = -1; kit.classList.add('ms-fonts'); };
    if ('ResizeObserver' in window) new ResizeObserver(function () { loopW = -1; }).observe(track);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fontsIn, fontsIn);
    else fontsIn();
  }

  /* Page-load cost: a strip sets itself up only once it is within a screen of the viewport and the page is
     idle (or at once if it is touched, clicked or focused first, so "+ Add" always works). */
  function soon(kit) {
    if (kit.__msInit || kit.__msSoon) return;
    kit.__msSoon = true;
    /* requestIdleCallback hands over a deadline, not an event */
    var now = function (e) { if (e && e.type && e.type !== 'mouseenter') kit.__msFirst = e; init(kit); };
    ['pointerdown', 'touchstart', 'focusin', 'click', 'wheel', 'mouseenter'].forEach(function (t) {
      kit.addEventListener(t, now, { capture: true, once: true, passive: true });
    });
    var idle = function () { (window.requestIdleCallback || function (f) { return setTimeout(f, 200); })(now, { timeout: 2000 }); };
    var whenLoaded = function () { if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true }); };
    if (!('IntersectionObserver' in window)) { whenLoaded(); return; }
    var io = new IntersectionObserver(function (es) {
      if (!es[es.length - 1].isIntersecting) return;
      io.disconnect();
      whenLoaded();
    }, { rootMargin: '100% 0px' });
    io.observe(kit);
  }
  function initAll(root) { Array.prototype.forEach.call((root || document).querySelectorAll('.ms-kit'), soon); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
